import * as React from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import {
    getRewardedPromptCampaign,
    getRewardedPromptCampaignStats,
    getRewardedPromptCampaignUserStats,
} from '../Api';
import type {
    RewardedPromptCampaignDto,
    RewardedPromptCampaignStatsDto,
    RewardedPromptUserStatsRowDto,
} from '../models/dtos/RewardedPromptCampaignDto';

export default function RewardedPromptCampaignStatsPage() {
    const id = useParams()['id'] as string;
    const navigate = useNavigate();
    const [loading, setLoading] = React.useState(true);
    const [campaign, setCampaign] = React.useState<RewardedPromptCampaignDto | null>(null);
    const [stats, setStats] = React.useState<RewardedPromptCampaignStatsDto | null>(null);
    const [users, setUsers] = React.useState<RewardedPromptUserStatsRowDto[]>([]);

    React.useEffect(() => {
        const load = async () => {
            setLoading(true);
            const [c, s, u] = await Promise.all([
                getRewardedPromptCampaign(id),
                getRewardedPromptCampaignStats(id),
                getRewardedPromptCampaignUserStats(id),
            ]);
            setCampaign(c);
            setStats(s);
            setUsers(u ?? []);
            setLoading(false);
        };
        load();
    }, [id]);

    if (loading) {
        return (
            <Box sx={{ mx: 3, my: 3 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ mx: 3, my: 3 }}>
            <Stack spacing={3}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h4">{campaign?.name ?? 'Rewarded prompt'} — stats</Typography>
                    <Button variant="outlined" onClick={() => navigate('/marketing/rewarded-prompts/' + encodeURIComponent(id) + '/edit')}>
                        Edit
                    </Button>
                </Stack>

                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                    <Card sx={{ flex: 1 }}>
                        <CardContent>
                            <Typography variant="overline">Impressions</Typography>
                            <Typography variant="h4">{stats?.totalImpressions ?? 0}</Typography>
                        </CardContent>
                    </Card>
                    <Card sx={{ flex: 1 }}>
                        <CardContent>
                            <Typography variant="overline">Dismissals</Typography>
                            <Typography variant="h4">{stats?.totalDismissals ?? 0}</Typography>
                        </CardContent>
                    </Card>
                    <Card sx={{ flex: 1 }}>
                        <CardContent>
                            <Typography variant="overline">Watch starts</Typography>
                            <Typography variant="h4">{stats?.totalWatchStarts ?? 0}</Typography>
                        </CardContent>
                    </Card>
                    <Card sx={{ flex: 1 }}>
                        <CardContent>
                            <Typography variant="overline">Watch completes</Typography>
                            <Typography variant="h4">{stats?.totalWatchCompletes ?? 0}</Typography>
                        </CardContent>
                    </Card>
                    <Card sx={{ flex: 1 }}>
                        <CardContent>
                            <Typography variant="overline">Watch CTR</Typography>
                            <Typography variant="h4">{((stats?.watchCtr ?? 0) * 100).toFixed(1)}%</Typography>
                        </CardContent>
                    </Card>
                </Stack>

                <Card>
                    <CardContent>
                        <Typography variant="h6" sx={{ mb: 2 }}>Users</Typography>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>User ref</TableCell>
                                    <TableCell>Impressions</TableCell>
                                    <TableCell>Dismissals</TableCell>
                                    <TableCell>Watch starts</TableCell>
                                    <TableCell>Watch completes</TableCell>
                                    <TableCell>Last shown</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {users.map(row => (
                                    <TableRow key={row.userRef}>
                                        <TableCell>{row.userRef}</TableCell>
                                        <TableCell>{row.impressionsCount}</TableCell>
                                        <TableCell>{row.dismissalsCount}</TableCell>
                                        <TableCell>{row.watchStartsCount}</TableCell>
                                        <TableCell>{row.watchCompletesCount}</TableCell>
                                        <TableCell>{row.lastShownAt ? new Date(row.lastShownAt).toLocaleString() : '—'}</TableCell>
                                    </TableRow>
                                ))}
                                {users.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={6}>No user stats yet.</TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>
            </Stack>
        </Box>
    );
}
